import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography
} from 'antd';
import type { FormInstance } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import CustomerContactsFormList from './CustomerContactsFormList';
import { EU_COUNTRIES, VATValidationResult } from '../../utils/vatValidation';
import {
  getPrimaryCustomerContact,
  normalizeCustomerContacts,
  toCustomerContactFormList
} from '../../utils/customerContacts';
import { DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS } from '../../modules/orders/customerPayment';
import { getVatState, getVatValidationTranslation } from '../../utils/customerVatStatus';
import type {
  Customer,
  CustomerFormValues,
  CustomerSubmitValues
} from '../../modules/customers/types';

const { Text } = Typography;

interface CustomerFormProps {
  form: FormInstance<CustomerFormValues>;
  customers: Customer[];
  editingCustomer: Customer | null;
  canViewCustomerPayments: boolean;
  submitting: boolean;
  submitLabel: string;
  onSubmit: (values: CustomerSubmitValues) => void | Promise<void>;
  isTester?: boolean;
  /** Re-initialize fields when this flips to true (modal open / entering edit). */
  open?: boolean;
  /** Lock branch type and parent when opened from a company's "Add branch" action. */
  initialBranchParent?: Customer | null;
  onCancel?: () => void;
}

export function buildNewCustomerFormValues(
  initialBranchParent?: Customer | null
): Partial<CustomerFormValues> {
  return {
    contactPeople: toCustomerContactFormList(),
    isBranch: Boolean(initialBranchParent),
    parentCustomerId: initialBranchParent?.id || null,
    taxId: initialBranchParent?.taxId || '',
    defaultPaymentTermDays: DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS,
    sendOrderStatusEmails: false
  };
}

export function buildCustomerSubmitValues(
  values: CustomerFormValues,
  selectedCountry: string,
  canViewCustomerPayments: boolean
): CustomerSubmitValues {
  let taxId = values.taxId || null;
  if (taxId && selectedCountry && !taxId.toUpperCase().startsWith(selectedCountry)) {
    taxId = selectedCountry + taxId;
  }

  const contactPeople = normalizeCustomerContacts(values.contactPeople);
  const primaryContact = getPrimaryCustomerContact(contactPeople);

  const cleanedValues: CustomerSubmitValues = {
    name: values.name,
    email: values.email || null,
    contactPeople,
    contactPerson: primaryContact?.name || null,
    phone: primaryContact?.phone || null,
    taxId,
    address: values.address || null,
    regon: values.regon || null,
    isBranch: Boolean(values.isBranch),
    parentCustomerId: values.isBranch ? values.parentCustomerId || null : null,
    sendOrderStatusEmails: Boolean(values.sendOrderStatusEmails) && Boolean(values.email)
  };

  if (canViewCustomerPayments) {
    cleanedValues.defaultPaymentTermDays = values.defaultPaymentTermDays ?? DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS;
  }

  return cleanedValues;
}

/**
 * Shared customer create/edit form. Owns VAT country, NIP lookup, branch
 * wiring and the normalization of submit values; consumers (list create modal,
 * detail page) only supply `editingCustomer` and handle the API call in
 * `onSubmit`, plus field-level error mapping on their shared `form` instance.
 */
const CustomerForm: React.FC<CustomerFormProps> = ({
  form,
  customers,
  editingCustomer,
  canViewCustomerPayments,
  submitting,
  submitLabel,
  onSubmit,
  isTester = false,
  open = true,
  initialBranchParent = null,
  onCancel
}) => {
  const { t } = useTranslation(['customers', 'common']);
  const { message } = App.useApp();
  const [selectedCountry, setSelectedCountry] = useState<string>('PL');
  const [vatValidation, setVatValidation] = useState<VATValidationResult | null>(null);
  const [vatValidating, setVatValidating] = useState(false);
  const [nipFetching, setNipFetching] = useState(false);

  const isBranchFieldValue = Form.useWatch('isBranch', form);
  const parentCustomerIdFieldValue = Form.useWatch('parentCustomerId', form);

  const parentCustomerOptions = useMemo(
    () =>
      customers
        .filter(
          (customer) =>
            !customer.deletedAt && !customer.isBranch && customer.id !== editingCustomer?.id
        )
        .map((customer) => ({
          value: customer.id,
          label: customer.name,
          taxId: customer.taxId || null
        })),
    [customers, editingCustomer?.id]
  );

  const selectedParentCustomer = useMemo(
    () => customers.find((customer) => customer.id === parentCustomerIdFieldValue) || null,
    [customers, parentCustomerIdFieldValue]
  );

  // Initialize form fields + VAT UI from the customer being edited (or blanks
  // for a new customer) whenever the form is (re)opened.
  useEffect(() => {
    if (!open) return;

    if (editingCustomer) {
      form.setFieldsValue({
        name: editingCustomer.name,
        contactPeople: toCustomerContactFormList(
          editingCustomer.contactPeople,
          editingCustomer.contactPerson,
          editingCustomer.phone
        ),
        email: editingCustomer.email || '',
        taxId: editingCustomer.taxId || '',
        address: editingCustomer.address || '',
        regon: editingCustomer.regon || '',
        defaultPaymentTermDays:
          editingCustomer.defaultPaymentTermDays ?? DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS,
        isBranch: Boolean(editingCustomer.isBranch),
        parentCustomerId: editingCustomer.parentCustomerId || null,
        sendOrderStatusEmails: Boolean(editingCustomer.sendOrderStatusEmails)
      });
      setSelectedCountry(editingCustomer.vatCountryCode || 'PL');
      setVatValidation(
        getVatState(editingCustomer) !== 'unchecked'
          ? {
              valid: Boolean(editingCustomer.vatValidFormat),
              countryCode: editingCustomer.vatCountryCode || null,
              vatNumber: editingCustomer.vatNumberFormat || editingCustomer.taxId || '',
              message: editingCustomer.vatValidationNote || ''
            }
          : null
      );
    } else {
      form.resetFields();
      form.setFieldsValue(buildNewCustomerFormValues(initialBranchParent));
      setSelectedCountry(initialBranchParent?.vatCountryCode || 'PL');
      setVatValidation(
        initialBranchParent && getVatState(initialBranchParent) !== 'unchecked'
          ? {
              valid: Boolean(initialBranchParent.vatValidFormat),
              countryCode: initialBranchParent.vatCountryCode || null,
              vatNumber: initialBranchParent.vatNumberFormat || initialBranchParent.taxId || '',
              message: initialBranchParent.vatValidationNote || ''
            }
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingCustomer]);

  const handleVatValidation = async () => {
    const taxId = form.getFieldValue('taxId');
    if (!taxId) {
      message.warning(t('customers:messages.validateFirst'));
      return;
    }

    setVatValidating(true);
    try {
      let vatNumber = String(taxId).toUpperCase();
      if (!vatNumber.startsWith(selectedCountry)) {
        vatNumber = selectedCountry + vatNumber;
      }

      const response = await api.post('/customers/validate-vat', { vatNumber });
      setVatValidation(response.data);
      form.setFieldValue('taxId', response.data.vatNumber);
      const validationText = t(getVatValidationTranslation(response.data).key);

      if (response.data.valid) {
        message.success(validationText);
      } else {
        message.warning(validationText);
      }
    } catch (error) {
      message.error(t('customers:messages.validationFailed'));
    } finally {
      setVatValidating(false);
    }
  };

  const handleNipLookup = async () => {
    const taxId = form.getFieldValue('taxId');
    if (!taxId) {
      message.warning(t('customers:messages.nipEnterFirst'));
      return;
    }

    setNipFetching(true);
    try {
      const response = await api.post('/customers/lookup-nip', { nip: taxId });
      const data = response.data;

      form.setFieldsValue({
        name: data.name,
        address: data.address || '',
        regon: data.regon || ''
      });

      if (data.nip) {
        form.setFieldValue('taxId', data.nip);
      }

      message.success(t('customers:messages.nipLookupSuccess'));
    } catch (error: any) {
      const code = error.response?.data?.code;
      if (code === 'NIP_INVALID') {
        message.error(t('customers:messages.nipInvalid'));
      } else if (code === 'NIP_NOT_FOUND') {
        message.warning(t('customers:messages.nipNotFound'));
      } else {
        message.error(t('customers:messages.nipLookupFailed'));
      }
    } finally {
      setNipFetching(false);
    }
  };

  const handleBranchToggle = (checked: boolean) => {
    if (!checked) {
      form.setFieldValue('parentCustomerId', null);
      setVatValidation(null);
      return;
    }

    const parentCustomerId = form.getFieldValue('parentCustomerId');
    if (!parentCustomerId) {
      form.setFieldValue('taxId', '');
    }
  };

  const handleParentCustomerChange = (parentCustomerId?: string) => {
    const parentCustomer = customers.find((customer) => customer.id === parentCustomerId) || null;

    if (!parentCustomer) {
      form.setFieldValue('taxId', '');
      setVatValidation(null);
      return;
    }

    form.setFieldValue('taxId', parentCustomer.taxId || '');
    setSelectedCountry(parentCustomer.vatCountryCode || 'PL');

    if (getVatState(parentCustomer) !== 'unchecked') {
      setVatValidation({
        valid: Boolean(parentCustomer.vatValidFormat),
        countryCode: parentCustomer.vatCountryCode || null,
        vatNumber: parentCustomer.vatNumberFormat || parentCustomer.taxId || '',
        message: parentCustomer.vatValidationNote || ''
      });
    } else {
      setVatValidation(null);
    }
  };

  const handleFinish = (values: CustomerFormValues) => {
    const valuesWithLockedBranch = initialBranchParent
      ? {
          ...values,
          isBranch: true,
          parentCustomerId: initialBranchParent.id,
          taxId: initialBranchParent.taxId || ''
        }
      : values;

    onSubmit(
      buildCustomerSubmitValues(
        valuesWithLockedBranch,
        selectedCountry,
        canViewCustomerPayments
      )
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onFinishFailed={() => message.warning(t('customers:messages.fillRequired'))}
      scrollToFirstError
    >
      <div className="customers-form-section">
        <Text className="customers-form-section__label">{t('customers:form.companySection')}</Text>

        {!isTester && initialBranchParent ? (
          <>
            <Form.Item name="isBranch" valuePropName="checked" hidden>
              <Switch />
            </Form.Item>
            <Form.Item name="parentCustomerId" hidden>
              <Input />
            </Form.Item>
            <Form.Item label={t('customers:form.parentCustomer')}>
              <Input value={initialBranchParent.name} disabled />
            </Form.Item>
            <Text type="secondary">
              {t('customers:form.branchTaxIdHintWithParent', {
                name: initialBranchParent.name
              })}
            </Text>
          </>
        ) : !isTester ? (
          <>
            <Form.Item name="isBranch" label={t('customers:form.isBranch')} valuePropName="checked">
              <Switch onChange={handleBranchToggle} />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.isBranch !== currentValues.isBranch ||
                prevValues.parentCustomerId !== currentValues.parentCustomerId
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('isBranch') ? (
                  <>
                    <Form.Item
                      name="parentCustomerId"
                      label={t('customers:form.parentCustomer')}
                      rules={[{ required: true, message: t('customers:messages.parentRequired') }]}
                    >
                      <Select
                        showSearch
                        placeholder={t('customers:form.selectParentCustomer')}
                        optionFilterProp="label"
                        options={parentCustomerOptions}
                        onChange={handleParentCustomerChange}
                      />
                    </Form.Item>

                    <Text type="secondary">
                      {selectedParentCustomer
                        ? t('customers:form.branchTaxIdHintWithParent', {
                            name: selectedParentCustomer.name
                          })
                        : t('customers:form.branchTaxIdHint')}
                    </Text>
                  </>
                ) : null
              }
            </Form.Item>
          </>
        ) : null}

        <Form.Item
          name="name"
          label={t('customers:form.name')}
          rules={[{ required: true, message: t('customers:validation.nameRequired') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label={t('customers:form.vatNumber')}>
          <Space.Compact style={{ width: '100%' }}>
            <Select value={selectedCountry} onChange={setSelectedCountry} style={{ width: '150px' }}>
              {EU_COUNTRIES.map((country) => (
                <Select.Option key={country.code} value={country.code}>
                  {country.code} - {country.name}
                </Select.Option>
              ))}
            </Select>

            <Form.Item name="taxId" noStyle>
              <Input
                placeholder={t('customers:form.enterVatNumber')}
                style={{ flex: 1 }}
                disabled={Boolean(isBranchFieldValue)}
              />
            </Form.Item>

            <Button
              icon={vatValidating ? <Spin size="small" /> : <ReloadOutlined />}
              onClick={handleVatValidation}
              disabled={vatValidating || Boolean(isBranchFieldValue)}
            >
              {t('customers:form.validate')}
            </Button>

            {selectedCountry === 'PL' && !isBranchFieldValue && (
              <Button
                icon={nipFetching ? <Spin size="small" /> : <SearchOutlined />}
                onClick={handleNipLookup}
                disabled={nipFetching}
              >
                {t('customers:form.lookupNip')}
              </Button>
            )}
          </Space.Compact>
        </Form.Item>

        {vatValidation && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                {vatValidation.valid ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    {t('customers:validation.validFormat')}
                  </Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />} color="error">
                    {t('customers:validation.invalidFormat')}
                  </Tag>
                )}
                <span>{t(getVatValidationTranslation(vatValidation).key)}</span>
              </Space>
            </Space>
          </div>
        )}

        <Form.Item name="regon" label={t('customers:form.regon')}>
          <Input />
        </Form.Item>

        <Form.Item name="address" label={t('customers:form.address')}>
          <Input.TextArea rows={3} />
        </Form.Item>

        {canViewCustomerPayments && (
          <Form.Item
            name="defaultPaymentTermDays"
            label={t('customers:form.defaultPaymentTermDays')}
            rules={[{ required: true, message: t('customers:validation.defaultPaymentTermRequired') }]}
          >
            <InputNumber
              min={0}
              max={365}
              precision={0}
              addonAfter={t('customers:form.days')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}
      </div>

      <div className="customers-form-section">
        <Text className="customers-form-section__label">{t('customers:form.contactSection')}</Text>

        <CustomerContactsFormList
          addLabel={t('customers:form.addContact')}
          contactLabel={t('customers:form.contactCardTitle')}
          nameLabel={t('customers:form.contactPerson')}
          phoneLabel={t('customers:form.contactPhone')}
          emailLabel={t('customers:form.contactEmail')}
          primaryLabel={t('customers:form.contactPrimary')}
          removeLabel={t('customers:form.removeContact')}
        />

        <Form.Item
          name="email"
          label={t('customers:form.contactEmail')}
          rules={[{ type: 'email', message: t('customers:validation.invalidEmail') }]}
        >
          <Input
            onChange={(e) => {
              if (!e.target.value) {
                form.setFieldValue('sendOrderStatusEmails', false);
              }
            }}
          />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.email !== currentValues.email}
        >
          {({ getFieldValue }) => {
            const emailEmpty = !getFieldValue('email');
            return (
              <Form.Item
                name="sendOrderStatusEmails"
                label={t('customers:form.sendOrderStatusEmails')}
                valuePropName="checked"
                tooltip={t('customers:form.sendOrderStatusEmailsHint')}
                extra={emailEmpty ? t('customers:form.sendOrderStatusEmailsNoEmail') : undefined}
              >
                <Switch disabled={emailEmpty} />
              </Form.Item>
            );
          }}
        </Form.Item>
      </div>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {submitLabel}
          </Button>
          {onCancel && (
            <Button onClick={onCancel} disabled={submitting}>
              {t('common:cancel')}
            </Button>
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default CustomerForm;
